import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
} from "@mui/material";
import { CorporateFare, ExpandMore, Add } from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "@/store";
import {
  fetchOrganizations,
  setActiveOrg,
} from "@/store/slices/organizationSlice";
import { CreateOrganizationDialog } from "./CreateOrganizationDialog";

export function OrganizationSwitcher() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { list, activeOrgId } = useAppSelector((s) => s.org);
  const { isAuthenticated } = useAppSelector((s) => s.auth);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchOrganizations());
    }
  }, [dispatch, isAuthenticated]);

  const activeOrg = list.find((o) => o.id === activeOrgId);

  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: { xs: 1, sm: 1.5 },
          py: 0.75,
          minWidth: 0,
          maxWidth: { xs: "100%", sm: 220, md: 280 },
          borderRadius: 1,
          bgcolor: "action.hover",
          cursor: "pointer",
          border: 1,
          borderColor: "divider",
          "&:hover": { bgcolor: "action.selected" },
        }}
      >
        <CorporateFare
          fontSize="small"
          color="primary"
          sx={{ flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontWeight: 600,
            minWidth: 0,
            color: "text.primary",
          }}
        >
          {activeOrg?.displayName || activeOrg?.name || t("org.switchOrg")}
        </Typography>
        <ExpandMore
          fontSize="small"
          sx={{ color: "text.secondary", flexShrink: 0 }}
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        {list.map((org) => (
          <MenuItem
            key={org.id}
            selected={org.id === activeOrgId}
            onClick={() => {
              dispatch(setActiveOrg(org.id));
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <CorporateFare fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={org.displayName || org.name}
              slotProps={{ primary: { sx: { fontSize: 14 } } }}
            />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setCreateOpen(true);
          }}
        >
          <ListItemIcon>
            <Add fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("org.createOrg")}
            slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 600 } } }}
          />
        </MenuItem>
      </Menu>
      <CreateOrganizationDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </>
  );
}
